// Report form component

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  VStack,
  Button,
  HStack,
} from '@chakra-ui/react';
import { reportFormSchema, ReportFormData } from '@/client/utils/validationSchemas';
import { FormField } from '@/components/Modal/FormField';

interface ReportFormProps {
  initialData?: Partial<ReportFormData>;
  onSubmit: (data: ReportFormData) => Promise<void>;
  onCancel?: () => void;
}

/**
 * Form for adding/editing reports
 */
export function ReportForm({ initialData, onSubmit, onCancel }: ReportFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ReportFormData>({
    resolver: zodResolver(reportFormSchema),
    defaultValues: {
      title: initialData?.title || '',
      description: initialData?.description || '',
      public_url: initialData?.public_url || '',
    },
  });

  const handleFormSubmit = async (data: ReportFormData) => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <VStack spacing={4} align="stretch" >
        <FormField
          label="Report Title"
          name="title"
          register={register}
          error={errors.title}
          placeholder="Enter report title"
          required
        />

        <FormField
          label="Description"
          name="description"
          register={register}
          error={errors.description}
          type="textarea"
          placeholder="Describe what this report shows..."
          required
        />

        <FormField
          label="Metabase Dashboard URL"
          name="public_url"
          register={register}
          error={errors.public_url}
          type="text"
          placeholder="https://taskeye.metabaseapp.com/public/dashboard/..."
          required
        />

        <HStack justify="flex-end" spacing={3} pt={2}>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="brand"
            isLoading={isSubmitting}
            loadingText="Saving..."
          >
            {initialData ? 'Update Report' : 'Create Report'}
          </Button>
        </HStack>
      </VStack>
    </form>
  );
}
